import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaChevronLeft, FaChevronRight, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import servicesData from "../../data/services.json";

function ServiceCard({ item, type }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-md hover:shadow-xl
                 transition-all duration-300
                 w-full md:w-[280px] h-[410px]
                 flex flex-col overflow-hidden"
    >
      <div className="h-[170px] bg-gray-100 overflow-hidden">
        <img
          src={item.img}
          alt={item.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="px-4 sm:px-5 pt-4 pb-5 flex flex-col flex-grow">
        <h4
          className="text-sm sm:text-base font-semibold text-gray-900
                       leading-snug line-clamp-2 mb-1 min-h-[44px]"
        >
          {item.title}
        </h4>

        <p
          className="text-xs sm:text-sm text-gray-600
                      leading-relaxed line-clamp-3 min-h-[56px]"
        >
          {item.desc}
        </p>

        <Link
          to={`/${type}/${item.slug}`}
          className="mt-5 bg-blue-600 text-white text-center py-2 sm:py-2.5 rounded-lg
                     text-xs sm:text-sm font-semibold hover:bg-blue-700 transition"
        >
          View Details
        </Link>
      </div>
    </div>
  );
}

function HorizontalSlider({ title, items, type, viewAllLink }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const extendedItems = [...items, ...items, ...items];
  const startOffset = items.length;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    setCurrentIndex(startOffset);
  }, [startOffset]);

  useEffect(() => {
    if (currentIndex >= startOffset + items.length) {
      setTimeout(() => setCurrentIndex(startOffset), 300);
    } else if (currentIndex < startOffset) {
      setTimeout(() => setCurrentIndex(startOffset + items.length - 1), 300);
    }
  }, [currentIndex, items.length, startOffset]);

  return (
    <div className="mb-16 sm:mb-20">
      <div className="flex justify-between items-center mb-8">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">{title}</h3>

        <Link
          to={viewAllLink}
          className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white
                     rounded-full text-xs sm:text-sm font-semibold hover:bg-blue-700 transition whitespace-nowrap"
        >
          View All <FaArrowRight />
        </Link>
      </div>

      <div className="relative">
        <button
          onClick={() => setCurrentIndex((p) => p - 1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10
             p-2 text-gray-600 hover:text-blue-600 transition"
        >
          <FaChevronLeft size={22} />
        </button>

        <div className="overflow-hidden mx-10 sm:mx-12">
          <motion.div
            className="flex"
            animate={{
              x: isMobile
                ? `-${currentIndex * 100}%`
                : `-${currentIndex * 304}px`,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {extendedItems.map((item, index) => (
              <div key={index} className="flex-shrink-0 w-full md:w-auto pr-4 sm:pr-6">
                <ServiceCard item={item} type={type} />
              </div>
            ))}
          </motion.div>
        </div>

        <button
          onClick={() => setCurrentIndex((p) => p + 1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10
             p-2 text-gray-600 hover:text-blue-600 transition"
        >
          <FaChevronRight size={22} />
        </button>
      </div>
    </div>
  );
}

export default function Services() {
  const { courses, internships } = servicesData;

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 sm:mb-16">
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">Services</h2>
          <div className="h-1.5 w-24 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full" />
          <p className="mt-6 text-base sm:text-lg text-gray-600 max-w-2xl">
            Explore our courses and internship programs designed to elevate your
            skills.
          </p>
        </div>

        <HorizontalSlider
          title="Courses"
          items={courses}
          type="courses"
          viewAllLink="/courses"
        />

        <HorizontalSlider
          title="Internships"
          items={internships}
          type="internships"
          viewAllLink="/internships"
        />
      </div>
    </section>
  );
}
